import { CANADA_PROVINCE_ABBREVIATIONS } from 'constants/CanadaProvinceTimezones';
import { US_STATE_ABBREVIATIONS } from 'constants/USStateTimezones';
import { Maybe } from 'types/UtilityTypes';

import { QuoteCountries } from '../types';
import { toTitleCase } from './formatting';

/**
 * Parses a location string into its components (zip, city, state, country)
 * Supports:
 * - 5-digit US ZIP codes
 * - 9-digit US ZIP+4 codes
 * - 6-character Canada postal codes
 * - City followed by Canadian postal without comma (e.g. "Peterborough K9J 6X7")
 * - City, State format (e.g., "Boston, MA")
 * - City, State ZIP format (e.g., "Boston, MA 02118")
 */
export function parseLocation(location: string): Maybe<{
  country: QuoteCountries;
  zip: string;
  city: string;
  state: string;
}> {
  const normalized = location.trim();

  const normalizedZipOnly = normalized.match(/^(\d{5})(?:-\d{4})?$/);
  // Check if input is a US ZIP or ZIP+4 code
  if (normalizedZipOnly) {
    return {
      country: QuoteCountries.USA,
      zip: normalizedZipOnly[1],
      city: '',
      state: '',
    };
  }

  if (!normalized) {
    return null;
  }

  // Check if input is a 6-character Canada postal code
  const canadaPostalCodeRegex =
    /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ][ ]?\d[ABCEGHJKLMNPRSTVWXYZ]\d$/i;
  if (canadaPostalCodeRegex.test(normalized)) {
    return {
      country: QuoteCountries.Canada,
      zip: normalized,
      city: '',
      state: '',
    };
  }

  // Parse city, state format (e.g. "Boston, MA")
  const match = normalized.match(/^([^,]+),\s*([A-Z]{2})$/i);
  if (match) {
    let isUSState,
      isCanadaProvince = false;
    if (match.length > 2) {
      isUSState = US_STATE_ABBREVIATIONS.includes(match[2]);
      isCanadaProvince = CANADA_PROVINCE_ABBREVIATIONS.includes(match[2]);
    }

    // Try matching with US state, then Canada province. If neither match, assume US state w/ typo
    return {
      country: isUSState
        ? QuoteCountries.USA
        : isCanadaProvince
          ? QuoteCountries.Canada
          : QuoteCountries.USA,
      zip: '',
      city: match[1].trim(),
      state: match[2].toUpperCase(),
    };
  }

  // Parse city, state ZIP format (e.g. "Boston, MA 02118")
  const cityStateZipMatch = normalized.match(
    /^([^,]+),\s*([A-Z]{2})\s+(\d{5})(?:-\d{4})?$/i
  );
  if (cityStateZipMatch) {
    return {
      country: QuoteCountries.USA,
      zip: cityStateZipMatch[3],
      city: cityStateZipMatch[1].trim(),
      state: cityStateZipMatch[2].toUpperCase(),
    };
  }

  // parse city, province, postal code format for Canada (e.g., "Mississauga, ON L5N2K7")
  const ca = normalized.match(
    /^([^,]+),\s*([A-Z]{2})\s+([ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ][ ]?\d[ABCEGHJKLMNPRSTVWXYZ]\d)$/i
  );
  if (ca) {
    return {
      country: QuoteCountries.Canada,
      zip: ca[3].toUpperCase(),
      city: ca[1].trim(),
      state: ca[2].toUpperCase(),
    };
  }

  // City name + Canadian postal, no comma (common in AI summaries e.g. "Peterborough K9J 6X7").
  // Parsed after comma-based patterns so "Mississauga, ON L5N2K7" is not misread as city "Mississauga, ON".
  const citySpaceCanadianPostalMatch = normalized.match(
    /^([^,]+)\s+([ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ][ ]?\d[ABCEGHJKLMNPRSTVWXYZ]\d)$/i
  );
  if (citySpaceCanadianPostalMatch) {
    return {
      country: QuoteCountries.Canada,
      zip: citySpaceCanadianPostalMatch[2].toUpperCase(),
      city: citySpaceCanadianPostalMatch[1].trim(),
      state: '',
    };
  }

  return null;
}

/**
 * Shape accepted by `formatStopsDisplay`. Loosened from `QuickQuoteResponseStop`
 * so we can also render unsubmitted form stops (`QuoteFormStop`), which only
 * have the raw `location` string until parsing happens at submit/retry time.
 */
type DisplayableStop = {
  city?: string;
  state?: string;
  zip?: string | number;
  location?: string;
};

/**
 * Formats an array of stops for display using arrow notation.
 * Examples:
 * - 2 stops: "Boston, MA → New York, NY"
 * - 3 stops: "Boston, MA → Hartford, CT → New York, NY"
 * - 4+ stops: "Boston, MA → Hartford, CT → Springfield, MA → New York, NY"
 *
 * Prefers structured city/state/zip fields, but falls back to the raw
 * `location` input so the collapsed batch card updates live as the user types
 * before submit-time parsing runs.
 */
export function formatStopsDisplay(stops: DisplayableStop[]): string {
  const getLocationString = (stop: DisplayableStop) => {
    const hasCity = Boolean(stop.city && stop.city.trim());
    const hasState = Boolean(stop.state && stop.state.trim());
    if (hasCity && hasState) {
      return `${toTitleCase(stop.city as string)}, ${stop.state}`;
    }
    if (stop.zip && String(stop.zip).trim()) {
      return String(stop.zip).trim();
    }
    if (hasCity) {
      return toTitleCase(stop.city as string);
    }
    if (hasState) {
      return stop.state as string;
    }
    // Fallback: raw form input (e.g. "Chicago, IL" typed but not yet parsed).
    if (stop.location && stop.location.trim()) {
      return stop.location.trim();
    }
    return '';
  };

  if (!stops || stops.length === 0) return '';

  const labels = stops.map(getLocationString).filter(Boolean);
  if (labels.length === 0) return '';
  return labels.join(' → ');
}
